const parseArray = value => {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeSelectedOptions = value =>
  parseArray(value)
    .filter(option => typeof option?.name === 'string' && typeof option?.value === 'string' && option.name.trim() && option.value.trim())
    .filter(option => !(option.name.toLowerCase() === 'title' && option.value.toLowerCase() === 'default title'))
    .map(({ name, value }) => ({ name, value }));

const serializeSelectedOptions = value => JSON.stringify(normalizeSelectedOptions(value));

// Only use a named matrix when every offered row has the same axes and an unambiguous combination.
const getOptionAxes = variants => {
  const rows = (variants || []).filter(variant => !variant.isHidden);
  if (!rows.length) return [];
  const first = normalizeSelectedOptions(rows[0].selectedOptions);
  if (!first.length || new Set(first.map(option => option.name)).size !== first.length) return [];
  const names = first.map(option => option.name);
  const combinations = new Set();
  for (const row of rows) {
    const options = normalizeSelectedOptions(row.selectedOptions);
    if (options.length !== names.length || !names.every((name, index) => options[index].name === name)) return [];
    const key = JSON.stringify(options.map(option => option.value));
    if (combinations.has(key)) return [];
    combinations.add(key);
  }
  return names.map(name => ({
    name,
    values: [...new Set(rows.map(row => normalizeSelectedOptions(row.selectedOptions).find(option => option.name === name).value))]
  }));
};

const matchesOptions = (variant, choices) =>
  normalizeSelectedOptions(variant.selectedOptions).every(option => !choices[option.name] || choices[option.name] === option.value);

const resolveVariant = (variants, choices) => {
  const axes = getOptionAxes(variants);
  if (
    !axes.length ||
    !axes.every(axis => choices[axis.name]) ||
    Object.keys(choices).some(name => choices[name] && !axes.some(axis => axis.name === name))
  )
    return null;
  const matches = variants.filter(variant => !variant.isHidden && matchesOptions(variant, choices));
  return matches.length === 1 ? matches[0] : null;
};

// A change keeps earlier choices and clears incompatible later ones; it never manufactures a variant.
const changeOption = (variants, choices, name, value) => {
  const axes = getOptionAxes(variants);
  const next = {};
  for (const axis of axes) {
    const choice = axis.name === name ? value : choices[axis.name];
    if (choice && variants.some(variant => !variant.isHidden && variant.isAvailable && matchesOptions(variant, { ...next, [axis.name]: choice })))
      next[axis.name] = choice;
  }
  return next;
};

const getOptionValues = (variants, choices, name) => {
  const axes = getOptionAxes(variants);
  const index = axes.findIndex(axis => axis.name === name);
  if (index < 0) return [];
  const earlierChoices = Object.fromEntries(
    axes
      .slice(0, index)
      .filter(axis => choices[axis.name])
      .map(axis => [axis.name, choices[axis.name]])
  );
  return axes[index].values.map(value => ({
    value,
    available: variants.some(variant => !variant.isHidden && variant.isAvailable && matchesOptions(variant, { ...earlierChoices, [name]: value }))
  }));
};

const isFieldVisible = (field, choices, answers) => {
  if (!field.when) return true;
  const value = (field.when.source === 'option' ? choices : answers)[field.when.key];
  return field.when.values ? field.when.values.includes(value) : value === field.when.value;
};

const getInitialCustomization = (fields = [], answers = {}) =>
  Object.fromEntries(
    fields
      .filter(field => answers[field.key] !== undefined || (field.required && field.type === 'select' && field.values.length === 1))
      .map(field => [field.key, answers[field.key] !== undefined ? answers[field.key] : field.values[0]])
  );

const getVisibleCustomizationFields = (fields, choices, answers) => {
  const activeAnswers = {};
  return fields.filter(field => {
    if (!isFieldVisible(field, choices, activeAnswers)) return false;
    activeAnswers[field.key] = answers[field.key];
    return true;
  });
};

// Definition keys map to merchant-approved line-item properties. Paid add-ons require a separate fulfillment contract.
const validateCustomizationFields = value => {
  if (value == null) return { fields: [] };
  const fields = parseArray(value);
  if (!Array.isArray(value) && typeof value !== 'string') return { error: 'Customization fields must be a list.' };
  if (typeof value === 'string') {
    try {
      if (!Array.isArray(JSON.parse(value))) return { error: 'Customization fields must be a list.' };
    } catch {
      return { error: 'Customization fields must be valid JSON.' };
    }
  }
  if (fields.length > 12) return { error: 'Use no more than 12 customization fields.' };
  const keys = new Set();
  for (const field of fields) {
    if (
      !field ||
      typeof field.key !== 'string' ||
      !field.key.trim() ||
      field.key.length > 80 ||
      ['__proto__', 'constructor', 'prototype'].includes(field.key) ||
      keys.has(field.key)
    )
      return { error: 'Customization keys must be unique and between 1 and 80 characters.' };
    keys.add(field.key);
    if (typeof field.label !== 'string' || !field.label.trim() || field.label.length > 100)
      return { error: 'Each customization field needs a label.' };
    if (!['text', 'select'].includes(field.type) || typeof field.required !== 'boolean')
      return { error: 'Customization fields support text or select and must specify required.' };
    if (field.type === 'text' && (!Number.isInteger(field.maxLength) || field.maxLength < 1 || field.maxLength > 200))
      return { error: 'Text fields need a maximum length between 1 and 200.' };
    if (
      field.type === 'select' &&
      (!Array.isArray(field.values) ||
        !field.values.length ||
        field.values.length > 50 ||
        field.values.some(value => typeof value !== 'string' || !value.trim() || value.length > 100) ||
        new Set(field.values).size !== field.values.length)
    )
      return { error: 'Select fields need unique allowed values.' };
    if (
      field.when &&
      (!['option', 'answer'].includes(field.when.source) ||
        typeof field.when.key !== 'string' ||
        Object.keys(field.when).some(key => !['source', 'key', 'value', 'values'].includes(key)) ||
        (field.when.values !== undefined
          ? field.when.value !== undefined ||
            !Array.isArray(field.when.values) ||
            !field.when.values.length ||
            field.when.values.length > 50 ||
            field.when.values.some(value => typeof value !== 'string' || !value || value.length > 100)
          : typeof field.when.value !== 'string' || !field.when.value || field.when.value.length > 100))
    )
      return { error: 'Customization dependencies must match an option or an earlier answer.' };
    if (field.when?.source === 'answer' && (!keys.has(field.when.key) || field.when.key === field.key))
      return { error: 'Customization dependencies must reference an earlier field.' };
    const allowed = ['key', 'label', 'type', 'required', 'maxLength', 'values', 'when'];
    if (Object.keys(field).some(key => !allowed.includes(key))) return { error: 'Unsupported customization field setting.' };
  }
  return { fields };
};

const validateCustomization = (fieldsValue, answers = {}, selectedOptions = []) => {
  const definition = validateCustomizationFields(fieldsValue);
  if (definition.error) return definition;
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return { error: 'Customization answers must be an object.' };
  const fields = definition.fields;
  if (Object.keys(answers).some(key => !fields.some(field => field.key === key)))
    return { error: 'Unknown customization answer. Review your selection.' };
  const choices = Object.fromEntries(normalizeSelectedOptions(selectedOptions).map(option => [option.name, option.value]));
  const properties = [];
  for (const field of getVisibleCustomizationFields(fields, choices, answers)) {
    const value = answers[field.key];
    if (value == null || value === '') {
      if (field.required) return { error: `Complete ${field.label}.` };
      continue;
    }
    if (
      typeof value !== 'string' ||
      Array.from(value).some(character => character.codePointAt(0) < 32 || character.codePointAt(0) === 127) ||
      !value.trim()
    )
      return { error: `Enter a valid ${field.label}.` };
    if (field.type === 'text' && Array.from(value).length > field.maxLength)
      return { error: `${field.label} must be ${field.maxLength} characters or fewer.` };
    if (field.type === 'select' && !field.values.includes(value)) return { error: `Choose an allowed ${field.label}.` };
    properties.push({ key: field.key, label: field.label, value });
  }
  return { properties };
};

const getConfigurationSummary = (selectedOptions, properties = [], optionSummaryFallback = '') => {
  const optionSummary = normalizeSelectedOptions(selectedOptions)
    .map(option => `${option.name}: ${option.value}`)
    .join(' · ');
  const answerSummary = parseArray(properties)
    .map(property => `${property.label || property.key}: ${property.value}`)
    .join(' · ');
  return [optionSummary || optionSummaryFallback, answerSummary].filter(Boolean).join(' · ');
};

const getShopifyLineItemProperties = (properties, isDraftOrder) => {
  const values = parseArray(properties).map(({ key, value }) => ({ key, value }));
  if (!values.length) return {};
  return isDraftOrder ? { customAttributes: values } : { properties: values.map(({ key, value }) => ({ name: key, value })) };
};

module.exports = {
  normalizeSelectedOptions,
  serializeSelectedOptions,
  getOptionAxes,
  resolveVariant,
  changeOption,
  getOptionValues,
  getInitialCustomization,
  getVisibleCustomizationFields,
  validateCustomizationFields,
  validateCustomization,
  getConfigurationSummary,
  getShopifyLineItemProperties
};
